/**
 * SmartImage — a drop-in <img> replacement that renders a shimmer
 * skeleton until the image finishes loading, then cross-fades the
 * photo in. Falls back gracefully to a subtle broken-image glyph so
 * a 404 doesn't leave an ugly white square in the browse grid.
 *
 * Design goals:
 *  - Zero layout shift: consumer sets the aspect via `className`
 *    (usually `aspect-square` or `h-full w-full object-cover`), we
 *    render the skeleton in an absolutely-positioned overlay so the
 *    image slot never resizes.
 *  - Graceful stagger: browsers naturally decode images at different
 *    rates, so 6-16 cards in a grid show a nice ripple of reveals
 *    even without any hand-rolled animation.
 *  - Reduced-motion friendly: shimmer animation is CSS-only and
 *    disabled by `prefers-reduced-motion`.
 *  - Native lazy-load by default.
 */
import React from "react";
import { ImageOff } from "lucide-react";

interface Props extends React.ImgHTMLAttributes<HTMLImageElement> {
  /** Optional wrapper class (padding, aspect, rounded corners). */
  wrapperClassName?: string;
  /** Test id applied to the outer wrapper for grid smoke tests. */
  testId?: string;
  /** Fallback element rendered when the src fails to load. */
  errorFallback?: React.ReactNode;
}

export const SmartImage: React.FC<Props> = ({
  src,
  alt = "",
  className = "",
  wrapperClassName = "",
  testId,
  errorFallback,
  loading = "lazy",
  decoding = "async",
  onLoad,
  onError,
  ...imgProps
}) => {
  const [state, setState] = React.useState<"loading" | "loaded" | "error">(
    "loading",
  );

  // Reset state whenever src changes so gallery swaps re-trigger the skeleton.
  React.useEffect(() => { setState("loading"); }, [src]);

  return (
    <span
      className={`relative block overflow-hidden ${wrapperClassName}`}
      data-testid={testId || "smart-image"}
      data-state={state}
    >
      {state !== "loaded" && (
        <span
          aria-hidden
          data-testid="smart-image-skeleton"
          className={`absolute inset-0 sg-image-shimmer ${
            state === "error" ? "opacity-0" : "opacity-100"
          } transition-opacity duration-300`}
        />
      )}
      {state === "error" ? (
        errorFallback || (
          <span className="absolute inset-0 flex items-center justify-center bg-slate-50 dark:bg-slate-800 text-slate-300 dark:text-slate-600">
            <ImageOff className="h-6 w-6" strokeWidth={1.5} />
          </span>
        )
      ) : (
        <img
          {...imgProps}
          src={src}
          alt={alt}
          loading={loading}
          decoding={decoding}
          onLoad={(e) => {
            setState("loaded");
            onLoad?.(e);
          }}
          onError={(e) => {
            setState("error");
            onError?.(e);
          }}
          className={`${className} transition-opacity duration-500 ${
            state === "loaded" ? "opacity-100" : "opacity-0"
          }`}
        />
      )}
    </span>
  );
};

export default SmartImage;
